<template>
  <section class="bg-platin leading-[16px] tracking-wider sm:leading-[19px]">
    <div
      class="container grid grid-cols-4 items-end justify-center gap-x-6 gap-y-10 px-4 py-7 sm:items-start sm:py-8 xl:px-24 xl:py-9"
    >
      <!-- Logo -->
      <div class="bottom-footer__logo">
        <AgsLink
          type="loud"
          :to="toLocalePath('/')"
          :title="$t('header.logo_title')"
          :aria-label="$t('header.logo_title')"
          @click="scrollToTop"
        >
          <IconFooterLogo
            data-test-id="footer-logo"
            class="bottom-footer__logo-img"
          />
        </AgsLink>
      </div>
      <!-- General info -->
      <div class="bottom-footer__general-info">
        <div class="bottom-footer__general-info-links">
          <template v-for="(item, idx) in visibleLinks" :key="idx">
            <AgsLink
              v-if="getBottomLinkTo(item)"
              data-test-id="bottom-footer-item-link"
              class="inline-flex min-h-6 items-center px-1"
              :to="getBottomLinkTo(item)"
              @click="trackNavigation(item)"
            >
              {{ getBottomLinkLabel(item) }}
              <span
                v-if="idx + 1 < (visibleLinks || []).length"
                class="bottom-footer__general-info-divider"
                >|</span
              >
            </AgsLink>
            <span
              v-else
              class="inline-flex min-h-6 items-center px-1 text-sm font-bold tracking-wider sm:text-base"
            >
              {{ getBottomLinkLabel(item) }}
              <span
                v-if="idx + 1 < (visibleLinks || []).length"
                class="bottom-footer__general-info-divider"
                >|</span
              >
            </span>
          </template>
        </div>
        <div v-if="blok" class="bottom-footer__general-info-text">
          <p v-if="blok.tax_info_1">{{ blok.tax_info_1 }}</p>
          <p v-if="blok.tax_info_2">{{ blok.tax_info_2 }}</p>
        </div>
      </div>
      <!-- Social links -->
      <div
        class="bottom-footer__social-links"
        role="region"
        aria-labelledby="social-links-header"
      >
        <div id="social-links-header" class="bottom-footer__social-link-header">
          {{ blok.social_label }}
        </div>
        <div class="bottom-footer__social-link-wrapper">
          <!-- Legacy hardcoded icons — migrated into `socialLinks` incrementally, removed in a later ticket -->
          <a
            v-if="instagramLink"
            :href="instagramLink"
            target="_blank"
            :aria-label="getTargetBlankLabel('Instagram', $t)"
          >
            <IconInstagram
              data-test-id="footer-instagram"
              alt="Instagram"
              icon="instagram"
              class="bottom-footer__social-icon"
            />
          </a>
          <a
            v-if="facebookLink"
            :href="facebookLink"
            target="_blank"
            :aria-label="getTargetBlankLabel('Facebook', $t)"
          >
            <IconFacebook
              data-test-id="footer-facebook"
              alt="Facebook"
              icon="facebook"
              class="bottom-footer__social-icon"
            />
          </a>
          <a
            v-if="!isFinland && linkedinLink"
            :href="linkedinLink"
            target="_blank"
            :aria-label="getTargetBlankLabel('LinkedIn', $t)"
          >
            <IconLinkedin
              data-test-id="footer-linkedin"
              alt="LinkedIn"
              icon="linkedin"
              class="bottom-footer__social-icon"
            />
          </a>
          <template v-for="(item, idx) in socialLinks" :key="`social-${idx}`">
            <a
              v-if="item.href"
              :href="item.href"
              target="_blank"
              :aria-label="getTargetBlankLabel(item.title, $t)"
              data-test-id="footer-social-generic-item"
            >
              <NuxtImg
                :src="item.icon"
                :alt="item.title"
                :title="item.title"
                class="bottom-footer__social-icon-generic"
              />
            </a>
            <NuxtImg
              v-else
              :src="item.icon"
              :alt="item.title"
              :title="item.title"
              class="bottom-footer__social-icon-generic"
              data-test-id="footer-social-generic-item"
            />
          </template>
        </div>
        <div class="mt-2 text-sm">
          &copy; {{ new Date().getFullYear() }} {{ blok.copyright }}
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import useCountryCheck from '#layers/base/composables/useCountryCheck'
import type { SbFooter } from '~/cms/storyblok/types/storyblok.d.ts'
import { getTargetBlankLabel } from '#layers/base/utils/a11y'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

const { trackNavigationFooter } = useTrackingEvents()
const { t } = useI18n()

type BottomFooterProps = {
  blok: SbFooter
}

const props = defineProps<BottomFooterProps>()

const { isInList: isFinland } = useCountryCheck(['fi'])
const { toLocalePath } = useRouteHelpers()

const facebookLink =
  props.blok.facebook_link && 'url' in props.blok.facebook_link
    ? props.blok.facebook_link.url
    : ''
const instagramLink =
  props.blok.instagram_link && 'url' in props.blok.instagram_link
    ? props.blok.instagram_link.url
    : ''
const linkedinLink =
  props.blok.linkedin_link && 'url' in props.blok.linkedin_link
    ? props.blok.linkedin_link.url
    : ''

const visibleLinks = computed(() =>
  props.blok.bottom_links?.filter((link) => 'visible' in link && link.visible),
)

const getBottomLinkTo = (
  item: NonNullable<SbFooter['bottom_links']>[number],
) =>
  'link' in item && typeof item.link !== 'string' && item.link
    ? resolveStoryblokLink({ link: item.link })
    : ''

const getBottomLinkLabel = (
  item: NonNullable<SbFooter['bottom_links']>[number],
) => ('label' in item ? t(item.label || '') : '')

const socialLinks = computed(() =>
  (props.blok.socialLinks ?? []).reduce<
    { title: string; icon: string; href: string }[]
  >((acc, item) => {
    const icon = item.icon?.filename ?? ''
    if (item.visible && icon) {
      acc.push({
        title: item.title,
        icon,
        href: resolveStoryblokLink({ link: item.link }),
      })
    }
    return acc
  }, []),
)

const trackNavigation = (
  link: NonNullable<SbFooter['bottom_links']>[number],
) => {
  if ('label' in link && link.label) {
    trackNavigationFooter(link.label)
  }
}

const scrollToTop = () => window.scrollTo(0, 0)
</script>
<style lang="postcss" scoped>
.bottom-footer__logo {
  @apply order-2
  col-span-1
  flex
  sm:order-1;
}
.bottom-footer__general-info {
  @apply order-1
  col-span-4
  sm:order-2
  sm:col-span-2;
}
.bottom-footer__social-links {
  @apply order-3
  col-span-3
  sm:col-span-1;
}
.bottom-footer__logo-img {
  @apply h-auto
  w-full
  max-w-[183px]
  sm:max-w-[195px];
}
.bottom-footer__general-info-links {
  @apply break-words
  pb-5
  sm:pb-7;
}
.bottom-footer__general-info-links a {
  @apply gap-0
  font-bold
  text-sm
  tracking-wider
  sm:text-base;
}
.bottom-footer__general-info-divider {
  @apply px-2;
}
.bottom-footer__general-info-text {
  @apply text-sm
  sm:text-sm;
}
.bottom-footer__social-link-header {
  @apply font-bold
  text-sm
  pb-5
  sm:text-base
  sm:pb-7;
}
.bottom-footer__social-link-wrapper {
  @apply flex
  items-center
  gap-2;
}
.bottom-footer__social-icon-generic {
  @apply h-[26px]
  w-auto;
}
.bottom-footer__logo svg {
  @apply fill-white;
}
</style>
